import React from "react";
import { Paper, Typography, Box, Button, alpha } from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const GRADIENT_PRIMARY = "linear-gradient(135deg, #603F97 0%, #D31D83 100%)";
const GRADIENT_TEXT = "linear-gradient(45deg, #603F97 30%, #D31D83 90%)";

const NoDataAvailableUI = ({
  icon: Icon,
  title = "No Asset Data Yet",
  content = "Start building your asset portfolio by adding new entries or importing existing data.",
  subContent = "Your capital allowance calculations will appear here once you add data.",
  onAddNew,
  onImport,
  onGetHelp,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 6,
        textAlign: "center",
        bgcolor: "rgba(255, 255, 255, 0.98)",
        position: "relative",
        overflow: "hidden",
        minHeight: 400,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Icon Container */}
      <Box sx={{ position: "relative", mb: 3, zIndex: 1 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "12px",
            background: GRADIENT_PRIMARY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            boxShadow: "0 4px 10px rgba(96, 63, 151, 0.25)",
          }}
        >
          {Icon ? (
            <Icon sx={{ fontSize: 28, color: "white" }} />
          ) : (
            <InboxIcon sx={{ fontSize: 28, color: "white" }} />
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ zIndex: 1, mb: 4 }}>
        <Typography
          variant='h5'
          sx={{
            fontWeight: 700,
            background: GRADIENT_TEXT,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "1.35rem", sm: "1.5rem", md: "1.625rem" },
            lineHeight: 1.2,
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{ mb: 2, lineHeight: 1.6 }}
        >
          {content}
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ fontStyle: "italic", opacity: 0.8 }}
        >
          {subContent}
        </Typography>
      </Box>

      {/* Action Buttons */}
      {onAddNew && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            zIndex: 1,
          }}
        >
          <Button
            variant='contained'
            onClick={onAddNew}
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 20 }} />}
            sx={{
              py: 1.25,
              px: 3,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9375rem",
              background: GRADIENT_PRIMARY,
              boxShadow: (theme) =>
                `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
              "&:hover": {
                background: GRADIENT_PRIMARY,
                transform: "translateY(-2px)",
                boxShadow: "0 12px 30px rgba(96, 63, 151, 0.2)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Get Started
          </Button>
        </Box>
      )}

      {/* Help Section */}
      {onGetHelp && (
        <Box
          sx={{
            zIndex: 1,
            mt: 4,
            pt: 3,
            borderTop: "1px solid",
            borderColor: "divider",
            width: "100%",
            maxWidth: 400,
          }}
        >
          <Button
            variant='text'
            size='small'
            startIcon={<HelpOutlineIcon sx={{ fontSize: 18 }} />}
            onClick={onGetHelp}
            sx={{
              color: "text.secondary",
              textTransform: "none",
              "&:hover": {
                color: "primary.main",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            Need help getting started?
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default NoDataAvailableUI;
